import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Dropdown, Button, Empty, Input, Select, Modal, message, Flex, Tag, Tooltip, Spin } from "antd";
import {
  MoreOutlined,
  SearchOutlined,
  UploadOutlined,
  CloudUploadOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  TimesheetContainer,
  PageHeader,
  TabsContainer,
  TableContainer,
  EmptyState,
  MetricsSection,
  MetricCard,
} from "./Timesheet.style";
import TimesheetDetailsModal from "../../Admin/Timesheet/TimesheetDetailsModal";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;

const getMonthName = (monthNum) => {
  const months = ["January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];
  return months[monthNum - 1] || "";
};

const transformTimesheetData = (apiData) => {
  return apiData.map(ts => ({
    id: ts.id,
    talentId: ts.talentId,
    talentName: ts.talentName || ts.talentId,
    role: ts.role || "N/A",
    jobId: ts.jobId || "—",
    monthlyRate: parseFloat(ts.monthlyRate) || 0,
    partnerRate: parseFloat(ts.partnerRate) || 0,
    settledCost: parseFloat(ts.settledCost) || 0,
    partnerAmount: parseFloat(ts.partnerAmount) || 0,
    settledAmount: parseFloat(ts.settledAmount) || 0,
    month: ts.monthName || getMonthName(ts.month),
    monthNum: ts.month,
    year: String(ts.year),
    actualWorkingDays: ts.totalWorkingDays,
    leaveTaken: { 
      count: (ts.paidLeaveCount || 0) + (ts.unpaidLeaveCount || 0), 
      dates: ts.leaveDates || [] 
    },
    paidLeave: ts.paidLeaveCount || 0,
    unpaidLeave: ts.unpaidLeaveCount || 0,
    workingDays: ts.actualWorkingDays,
    billableHours: parseFloat(ts.billableHours) || 0,
    calculatedAmount: parseFloat(ts.calculatedAmount) || 0,
    status: ts.status,
    timesheetFileUrl: ts.timesheetFileUrl,
    submittedDate: ts.submittedAt ? new Date(ts.submittedAt).toISOString().split('T')[0] : null,
    approvedDate: ts.approvedAt ? new Date(ts.approvedAt).toISOString().split('T')[0] : null,
    rejectionReason: ts.rejectionReason,
    hiringRecordId: ts.hiringRecordId,
    workingHoursPerDay: ts.workingHoursPerDay || 8,
  }));
};

const PartnerTimesheet = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Pending Upload");
  const [searchText, setSearchText] = useState("");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [modalMode, setModalMode] = useState("edit");
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [myClientsOnly, setMyClientsOnly] = useState(
    () => sessionStorage.getItem("partnerTimesheet_myClientsOnly") === "true"
  );

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterYear) body.year = parseInt(filterYear);
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }

      if (myClientsOnly) {
        const relResponse = await axios.get(API_CONST.PARTNER_RELATION_MY_CLIENTS, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activeClients = (relResponse.data.relations || []).filter(
          (r) => r.status === "active" && r.customerOrganization
        );
        if (activeClients.length === 0) {
          setTimesheets([]);
          setLoading(false);
          return;
        }
        body.customerOrganizations = activeClients.map((r) => r.customerOrganization);
      }

      const response = await axios.post(API_CONST.GET_TIMESHEETS, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.timesheets) {
        const transformed = transformTimesheetData(response.data.timesheets);
        setTimesheets(transformed);
      }
    } catch (error) {
      console.error("Error fetching timesheets:", error);
      message.error("Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterMonth, myClientsOnly]);

  const handleMyClientsToggle = () => {
    const next = !myClientsOnly;
    setMyClientsOnly(next);
    sessionStorage.setItem("partnerTimesheet_myClientsOnly", String(next));
  };

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  const getFilteredData = () => {
    let filtered = [...timesheets];

    if (activeTab === "Pending Upload") {
      filtered = filtered.filter(t => t.status === "pending" || t.status === "submitted" || t.status === "rejected");
    } else if (activeTab === "Verified") {
      filtered = filtered.filter(t => t.status === "verified");
    } else if (activeTab === "Approved") {
      filtered = filtered.filter(t => t.status === "approved");
    }

    if (filterStatus) {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    if (searchText) {
      filtered = filtered.filter(t =>
        t.talentName.toLowerCase().includes(searchText.toLowerCase()) ||
        t.role.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return filtered;
  };

  const currentData = getFilteredData();

  const getMetrics = () => {
    const pending = timesheets.filter(t => t.status === "pending" || t.status === "submitted" || t.status === "rejected").length;
    const verified = timesheets.filter(t => t.status === "verified").length;
    const rejected = timesheets.filter(t => t.status === "rejected").length;
    const approved = timesheets.filter(t => t.status === "approved").length;
    const totalAmountPending = timesheets
      .filter(t => t.status === "pending" || t.status === "submitted" || t.status === "rejected")
      .reduce((sum, t) => sum + (t.partnerAmount || 0), 0);
    const totalAmountApproved = timesheets
      .filter(t => t.status === "approved")
      .reduce((sum, t) => sum + (t.settledAmount || 0), 0);

    return { pending, verified, rejected, approved, totalAmountPending, totalAmountApproved };
  };

  const metrics = getMetrics();

  const handleUploadClick = (record) => {
    setSelectedTalent(record);
    setModalMode("edit");
    setUploadModalVisible(true);
  };

  const handleUploadSubmit = async ({ leaveDates, paidLeaveCount, timesheetFile }) => {
    try {
      const token = localStorage.getItem("authToken");
      
      let timesheetFileUrl = selectedTalent.timesheetFileUrl;
      if (timesheetFile) {
        const uploadResponse = await axios.post(API_CONST.TIMESHEET_UPLOAD_REQUEST_URL, {
          timesheetId: selectedTalent.id,
          talentName: selectedTalent.talentName,
          name: timesheetFile.name,
          size: timesheetFile.size,
          contentType: timesheetFile.type
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (uploadResponse.data.uploadURL) {
          await axios.put(uploadResponse.data.uploadURL, timesheetFile, {
            headers: { "Content-Type": timesheetFile.type }
          });
          timesheetFileUrl = uploadResponse.data.objectPath;
        }
      }

      const isAlreadySubmitted = selectedTalent.status === 'submitted' || selectedTalent.status === 'verified';
      const endpoint = isAlreadySubmitted 
        ? `${API_CONST.UPDATE_TIMESHEET}/${selectedTalent.id}`
        : `${API_CONST.SUBMIT_TIMESHEET}/${selectedTalent.id}`;

      const response = await axios.put(endpoint, {
        leaveDates,
        paidLeaveCount,
        timesheetFileUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.timesheet) {
        message.success(`Timesheet ${isAlreadySubmitted ? 'updated' : 'submitted'} successfully for ${selectedTalent.talentName}`);
        fetchTimesheets();
        setUploadModalVisible(false);
        setSelectedTalent(null);
      }
    } catch (error) {
      console.error("Error submitting timesheet:", error);
      message.error(error.response?.data?.message || "Failed to submit timesheet");
    }
  };

  const handleDownload = async (record) => {
    if (!record.timesheetFileUrl) {
      message.warning("No timesheet file available for download");
      return;
    }
    
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(API_CONST.GET_TIMESHEET_DOWNLOAD_URL, {
        timesheetFileUrl: record.timesheetFileUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error("Error getting download URL:", error);
      message.error("Failed to download timesheet");
    }
  };

  const handleModify = (record) => {
    setSelectedTalent(record);
    setModalMode("edit");
    setUploadModalVisible(true);
  };

  const handleView = (record) => {
    setSelectedTalent(record);
    setModalMode("view");
    setUploadModalVisible(true);
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("authToken");
      const now = new Date();
      const response = await axios.post(API_CONST.SYNC_NOW, {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { timesheets: tsResult, invoices: invResult } = response.data;
      message.success(`Sync complete: ${tsResult.generated} timesheets generated, ${invResult.generated} invoices created`);
      fetchTimesheets();
    } catch (error) {
      console.error("Error syncing:", error);
      message.error(error.response?.data?.message || "Failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  const columns = [
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 160,
      ellipsis: true,
      render: (name, record) => record.talentId ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/partner/talent-details/${record.talentId}`)}
        >
          {name}
        </a>
      ) : <span className="talent-name">{name}</span>,
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 160,
      render: (id) => <span style={{ color: "#014c75", fontWeight: 500 }}>{id || "—"}</span>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 130,
      render: (text, record) => (
        <span>
          {text} {record.year}
          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            ({record.actualWorkingDays} days)
          </div>
        </span>
      ),
    },
    {
      title: "Leave Taken",
      key: "leaveTaken",
      width: 110,
      render: (_, record) => {
        const leaveTaken = record.leaveTaken;
        const count = leaveTaken?.count || 0;
        const dates = leaveTaken?.dates || [];
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{count} days</div>
            {count > 0 && dates.length > 0 && (
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                {dates.slice(0, 2).map(d => parseInt(d.split('-')[2], 10)).join(', ')}
                {dates.length > 2 && '...'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Working Days",
      dataIndex: "workingDays",
      key: "workingDays",
      width: 110,
    },
    {
      title: "Billable Hours",
      dataIndex: "billableHours",
      key: "billableHours",
      width: 110,
    },
    {
      title: "Partner Rate",
      dataIndex: "partnerAmount",
      key: "partnerAmount",
      width: 140,
      align: /** @type {'right'} */ ('right'),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#014c75", whiteSpace: "nowrap" }}>
          ₹ {Math.round(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Settled Cost",
      dataIndex: "settledAmount",
      key: "settledAmount",
      width: 140,
      align: /** @type {'right'} */ ('right'),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#00d9a9", whiteSpace: "nowrap" }}>
          ₹ {Math.round(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => {
        const statusColors = {
          pending: "orange",
          submitted: "blue",
          verified: "cyan",
          approved: "green",
          rejected: "red",
        };
        const statusLabels = {
          pending: "Pending",
          submitted: "Submitted",
          verified: "Verified",
          approved: "Approved",
          rejected: "Rejected",
        };
        return <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: /** @type {'right'} */ ('right'),
      render: (_, record) => {
        const items = [];

        if (record.status === "pending") {
          items.push({
            key: "upload",
            label: "Upload",
            icon: <UploadOutlined />,
            onClick: () => handleUploadClick(record),
          });
        }

        if (record.status === "submitted" || record.status === "rejected") {
          items.push({
            key: "modify",
            label: "Modify",
            icon: <EditOutlined />,
            onClick: () => handleModify(record),
          });
        }

        items.push({
          key: "view",
          label: "View",
          icon: <EyeOutlined />,
          onClick: () => handleView(record),
        });

        if (record.status === "submitted" || record.status === "verified" || record.status === "approved") {
          items.push({
            key: "download",
            label: "Download",
            icon: <DownloadOutlined />,
            onClick: () => handleDownload(record),
          });
        }

        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
          </Dropdown>
        );
      },
    },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <TimesheetContainer>
      <PageHeader>
        <h1>Timesheet</h1>
        <p>Upload and manage timesheets for your talents</p>
      </PageHeader>

      <MetricsSection>
        <MetricCard>
          <div className="metric-label">Pending Upload</div>
          <div className="metric-value">{metrics.pending}</div>
        </MetricCard>

        <MetricCard>
          <div className="metric-label">Approved</div>
          <div className="metric-value">{metrics.approved}</div>
        </MetricCard>

        <MetricCard>
          <div className="metric-label">Total Amount Pending</div>
          <div className="metric-value">
            ₹ {(metrics.totalAmountPending / 100000).toFixed(2)}L
          </div>
        </MetricCard>

        <MetricCard>
          <div className="metric-label">Total Amount Approved</div>
          <div className="metric-value">
            ₹ {(metrics.totalAmountApproved / 100000).toFixed(2)}L
          </div>
        </MetricCard>
      </MetricsSection>

      <TabsContainer>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            className={`tab-button ${activeTab === "Pending Upload" ? "active" : ""}`}
            icon={<CloudUploadOutlined />}
            onClick={() => setActiveTab("Pending Upload")}
          >
            Pending Upload ({metrics.pending})
          </Button>
          <Button
            className={`tab-button ${activeTab === "Verified" ? "active" : ""}`}
            icon={<FileDoneOutlined />}
            onClick={() => setActiveTab("Verified")}
          >
            Verified ({metrics.verified})
          </Button>
          <Button
            className={`tab-button ${activeTab === "Approved" ? "active" : ""}`}
            icon={<FileDoneOutlined />}
            onClick={() => setActiveTab("Approved")}
          >
            Approved ({metrics.approved})
          </Button>
        </div>
        <Button
          type="link"
          icon={<SyncOutlined spin={syncing} />}
          onClick={handleSyncNow}
          loading={syncing}
          style={{ padding: 0, color: '#1890ff' }}
        >
          Sync Now
        </Button>
      </TabsContainer>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name or role"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Month
              </span>
            }
            value={filterMonth || undefined}
            onChange={setFilterMonth}
            style={{ width: 200 }}
            allowClear
          >
            <Option value="January">January</Option>
            <Option value="February">February</Option>
            <Option value="March">March</Option>
            <Option value="April">April</Option>
            <Option value="May">May</Option>
            <Option value="June">June</Option>
            <Option value="July">July</Option>
            <Option value="August">August</Option>
            <Option value="September">September</Option>
            <Option value="October">October</Option>
            <Option value="November">November</Option>
            <Option value="December">December</Option>
          </Select>
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Year
              </span>
            }
            value={filterYear || undefined}
            onChange={setFilterYear}
            style={{ width: 150 }}
            allowClear
          >
            {yearOptions.map(year => (
              <Option key={year} value={String(year)}>{year}</Option>
            ))}
          </Select>
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Status
              </span>
            }
            value={filterStatus || undefined}
            onChange={setFilterStatus}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="pending">Pending</Option>
            <Option value="submitted">Submitted</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
        </div>
        <Button
          type={myClientsOnly ? "primary" : "default"}
          onClick={handleMyClientsToggle}
          style={myClientsOnly ? { background: "#014c75", borderColor: "#014c75" } : {}}
        >
          My Clients Only
        </Button>
      </div>

      <TableContainer>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : currentData.length > 0 ? (
          <Table
            columns={columns}
            dataSource={currentData}
            rowKey="id"
            pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
              pageSize: 10,
              showTotal: (total) => `Total ${total} records`,
            }}
            scroll={{ x: 1400 }}
          />
        ) : (
          <EmptyState>
            <Empty description="No timesheets found" />
          </EmptyState>
        )}
      </TableContainer>

      {uploadModalVisible && selectedTalent && (
        <TimesheetDetailsModal
          visible={uploadModalVisible}
          onClose={() => {
            setUploadModalVisible(false);
            setSelectedTalent(null);
          }}
          timesheet={selectedTalent}
          mode={modalMode}
          userRole="Partner"
          onSubmit={handleUploadSubmit}
        />
      )}
    </TimesheetContainer>
  );
};

export default PartnerTimesheet;
